import type { AccountInfo, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser'
import { currentEnvironment, graphScopes } from '../config/authConfig'

const PREFERRED_USERNAME_KEY = 'orbit:auth:preferred-username'
const AUTH_ERROR_KEY = 'orbit:auth:last-error'

function canUseStorage(storage: Storage | undefined): storage is Storage {
  if (!storage) return false

  try {
    const probeKey = 'orbit:auth:probe'
    storage.setItem(probeKey, '1')
    storage.removeItem(probeKey)
    return true
  } catch {
    return false
  }
}

function normalizeUsername(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase()
}

function readLocal(key: string): string {
  if (typeof window === 'undefined' || !canUseStorage(window.localStorage)) return ''
  return window.localStorage.getItem(key) || ''
}

function writeLocal(key: string, value: string): void {
  if (typeof window === 'undefined' || !canUseStorage(window.localStorage)) return
  window.localStorage.setItem(key, value)
}

function removeLocal(key: string): void {
  if (typeof window === 'undefined' || !canUseStorage(window.localStorage)) return
  window.localStorage.removeItem(key)
}

export function isExpectedTenantAccount(account: AccountInfo | null | undefined): account is AccountInfo {
  return !!account && normalizeUsername(account.tenantId) === normalizeUsername(currentEnvironment.tenantId)
}

export function rememberSuccessfulLogin(account: AccountInfo | null | undefined): void {
  if (!isExpectedTenantAccount(account)) return
  writeLocal(PREFERRED_USERNAME_KEY, normalizeUsername(account.username))
  clearAuthError()
}

export function getStoredLoginHint(): string {
  return readLocal(PREFERRED_USERNAME_KEY)
}

export function setAuthError(message: string): void {
  writeLocal(AUTH_ERROR_KEY, message)
}

export function getAuthError(): string {
  return readLocal(AUTH_ERROR_KEY)
}

export function clearAuthError(): void {
  removeLocal(AUTH_ERROR_KEY)
}

export function getExpectedTenantAccounts(instance: IPublicClientApplication, accounts?: AccountInfo[]): AccountInfo[] {
  const source = accounts || instance.getAllAccounts()
  return source.filter(isExpectedTenantAccount)
}

export function resolvePreferredAccount(instance: IPublicClientApplication, accounts?: AccountInfo[]): AccountInfo | null {
  const active = instance.getActiveAccount()
  if (isExpectedTenantAccount(active)) {
    return active
  }

  const expectedAccounts = getExpectedTenantAccounts(instance, accounts)
  if (expectedAccounts.length === 0) {
    return null
  }

  const preferredUsername = getStoredLoginHint()
  const matchedByUsername = preferredUsername
    ? expectedAccounts.find((account) => normalizeUsername(account.username) === preferredUsername)
    : null

  const resolved = matchedByUsername || expectedAccounts[0]
  instance.setActiveAccount(resolved)
  return resolved
}

export async function clearUnexpectedTenantAccounts(instance: IPublicClientApplication): Promise<void> {
  const unexpectedAccounts = instance.getAllAccounts().filter((account) => !isExpectedTenantAccount(account))

  await Promise.all(unexpectedAccounts.map(async (account) => {
    await instance.clearCache({ account }).catch(() => {})
  }))

  const active = instance.getActiveAccount()
  if (!isExpectedTenantAccount(active)) {
    instance.setActiveAccount(null)
  }
}

export function buildLoginRedirectRequest(loginHint?: string, prompt?: 'select_account' | 'login'): RedirectRequest {
  const normalizedLoginHint = normalizeUsername(loginHint) || getStoredLoginHint()

  return {
    authority: `https://login.microsoftonline.com/${currentEnvironment.tenantId}`,
    redirectUri: window.location.origin,
    scopes: [...graphScopes.user, ...graphScopes.sharepoint, ...graphScopes.mail],
    // Only force a prompt when explicitly requested (e.g. "Sign in as different user").
    // When we have a loginHint, omit prompt entirely so MSAL/Entra can use cached
    // browser sessions (SSO) without forcing re-auth + MFA on every visit.
    ...(prompt ? { prompt } : (normalizedLoginHint ? {} : { prompt: 'select_account' as const })),
    ...(normalizedLoginHint ? { loginHint: normalizedLoginHint, domainHint: 'organizations' } : {}),
  }
}
